<script setup>
import { $api } from '@/utils/api'

/**
 * pages/staff/pin-setup/[token].vue — Patch (kelanjutan Modul Y).
 *
 * Halaman PUBLIK (tanpa Sanctum/X-Tenant), diakses lewat link email yang
 * dikirim dari dialog "Detail Absensi" (User & Role). Otorisasinya berbasis
 * kepemilikan token di URL, sama prinsip dengan halaman self-order publik
 * (pages/order/scan/[qrToken].vue) — TIDAK pakai useApi() (yang otomatis
 * kirim cookie Authorization/X-Tenant staff yang sedang login di browser
 * ini, kalau ada) — panggil $api langsung TANPA header auth apa pun,
 * supaya request ini murni diautentikasi oleh token di URL.
 *
 * QR Code di-generate DI BROWSER dari `qr_token` yang dikirim backend
 * (reuse package `qrcode`, sama persis dengan StaffAttendanceCredentialDialog.vue
 * di sisi admin) — backend TIDAK PERNAH menggambar QR/kirim gambar apa pun,
 * sesuai keputusan: QR ditampilkan di halaman ini, bukan ditempel di email.
 */
definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const route = useRoute()
const token = route.params.token

const API_BASE = '/api/public/staff-pin-setup'

// ── State: load link ───────────────────────────────────────────────────
const loading = ref(true)
const loadErrorMessage = ref('')
const staffName = ref('')
const tenantName = ref('')
const qrToken = ref(null)
const expiresAt = ref(null)

const fetchLink = async () => {
  loading.value = true
  loadErrorMessage.value = ''
  try {
    const response = await $api(`${API_BASE}/${token}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })

    const data = response?.data ?? response

    staffName.value = data?.staff_name ?? ''
    tenantName.value = data?.tenant_name ?? ''
    qrToken.value = data?.qr_token ?? null
    expiresAt.value = data?.expires_at ?? null
  } catch (err) {
    loadErrorMessage.value = err?.data?.message ?? 'Link tidak valid atau sudah kedaluwarsa.'
  } finally {
    loading.value = false
  }
}

// ── QR image (reuse package `qrcode`, sama seperti dialog admin) ────────
const qrDataUrl = ref(null)
const qrLibMissing = ref(false)
const generatingQr = ref(false)

const generateQrImage = async () => {
  if (!qrToken.value) return

  generatingQr.value = true
  try {
    const QRCode = await import('qrcode')

    qrDataUrl.value = await QRCode.toDataURL(qrToken.value, { width: 260, margin: 2 })
  } catch (err) {
    console.warn('[staff/pin-setup] Package "qrcode" belum ter-install atau gagal generate:', err)
    qrLibMissing.value = true
  } finally {
    generatingQr.value = false
  }
}

const downloadQr = () => {
  if (!qrDataUrl.value) return
  const link = document.createElement('a')

  link.href = qrDataUrl.value
  link.download = `qr-absen-${staffName.value || 'staff'}.png`
  link.click()
}

// ── Form set PIN ──────────────────────────────────────────────────────────
const pin = ref('')
const pinConfirmation = ref('')
const showPin = ref(false)
const submitting = ref(false)
const submitErrors = ref({})
const submitSuccess = ref(false)

const submitPin = async () => {
  submitting.value = true
  submitErrors.value = {}
  try {
    await $api(`${API_BASE}/${token}`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: { pin: pin.value, pin_confirmation: pinConfirmation.value },
    })
    submitSuccess.value = true
  } catch (err) {
    // PENTING: 422 di app ini BUKAN cuma dipakai utk validasi field
    // (SetPinRequest) -- abort(404/410) di ResolveStaffPinSetupLink (mis.
    // link sudah dipakai duluan di tab lain / kedaluwarsa pas isi form) juga
    // ke-render sebagai 422 BUSINESS_RULE_VIOLATION (HttpException turunan
    // RuntimeException, lihat catatan bootstrap/app.php di CLAUDE.md) --
    // TIDAK bisa dibedakan cuma dari status code, harus dari ADA/TIDAKNYA
    // key `errors` di body. Ada errors -> field-level (tampil di textfield).
    // Tidak ada -> link-level (redirect ke tampilan error, bukan textfield).
    if (err?.data?.errors) {
      submitErrors.value = err.data.errors
    } else {
      loadErrorMessage.value = err?.data?.message ?? 'Link sudah tidak berlaku. Minta admin kirim ulang.'
    }
  } finally {
    submitting.value = false
  }
}

onMounted(async () => {
  await fetchLink()
  if (qrToken.value) await generateQrImage()
})
</script>

<template>
  <div class="d-flex align-center justify-center pa-4" style="min-block-size: 100vh;">
    <VCard max-width="420" class="pa-2" elevation="4">
      <VCardText class="text-center">
        <!-- Loading -->
        <div v-if="loading" class="py-10">
          <VProgressCircular indeterminate color="primary" size="40" />
        </div>

        <!-- Error (link tidak valid/kedaluwarsa) -->
        <template v-else-if="loadErrorMessage && !submitSuccess">
          <VIcon icon="ri-error-warning-line" size="48" color="error" class="mb-3" />
          <h5 class="text-h5 font-weight-bold mb-2">
            Link Tidak Valid
          </h5>
          <p class="text-body-2 text-medium-emphasis mb-0">
            {{ loadErrorMessage }}
          </p>
        </template>

        <!-- Sukses -->
        <template v-else-if="submitSuccess">
          <VIcon icon="ri-checkbox-circle-line" size="48" color="success" class="mb-3" />
          <h5 class="text-h5 font-weight-bold mb-2">
            PIN Berhasil Diatur
          </h5>
          <p class="text-body-2 text-medium-emphasis mb-4">
            PIN Anda sudah aktif. Simpan/unduh QR Code di bawah ini untuk absen
            di kiosk — atau langsung pakai PIN yang baru saja Anda atur.
          </p>

          <div class="qr-preview mb-3">
            <img v-if="qrDataUrl" :src="qrDataUrl" alt="QR Absensi" width="220" height="220">
          </div>
          <VBtn
            size="small"
            variant="tonal"
            prepend-icon="ri-download-2-line"
            :disabled="!qrDataUrl"
            class="mb-2"
            @click="downloadQr"
          >
            Unduh QR
          </VBtn>
        </template>

        <!-- Form isi PIN -->
        <template v-else>
          <h5 class="text-h5 font-weight-bold mb-1">
            Halo, {{ staffName }}
          </h5>
          <p class="text-body-2 text-medium-emphasis mb-5">
            Atur PIN absensi Anda untuk {{ tenantName }}. Link ini hanya bisa
            dipakai satu kali.
          </p>

          <div class="qr-preview mb-4">
            <VProgressCircular v-if="generatingQr" indeterminate color="primary" />
            <img v-else-if="qrDataUrl" :src="qrDataUrl" alt="QR Absensi" width="180" height="180">
            <VAlert v-else-if="qrLibMissing" type="warning" variant="tonal" density="compact">
              QR Code tidak bisa ditampilkan di perangkat ini.
            </VAlert>
          </div>
          <p class="text-caption text-medium-emphasis mb-5">
            QR Code di atas untuk absen di kiosk — screenshot atau unduh setelah PIN berhasil diatur.
          </p>

          <VTextField
            v-model="pin"
            label="PIN Baru"
            :type="showPin ? 'text' : 'password'"
            placeholder="4-6 digit"
            inputmode="numeric"
            maxlength="6"
            :error-messages="submitErrors.pin"
            class="mb-3"
          />
          <VTextField
            v-model="pinConfirmation"
            label="Konfirmasi PIN"
            :type="showPin ? 'text' : 'password'"
            placeholder="Ulangi PIN"
            inputmode="numeric"
            maxlength="6"
            :append-inner-icon="showPin ? 'ri-eye-off-line' : 'ri-eye-line'"
            class="mb-4"
            @click:append-inner="showPin = !showPin"
          />

          <VBtn block color="primary" :loading="submitting" @click="submitPin">
            Simpan PIN
          </VBtn>
        </template>
      </VCardText>
    </VCard>
  </div>
</template>

<style scoped>
.qr-preview {
  min-block-size: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
